import React, { useState, useEffect } from 'react';
import { API_BASE, authHeaders } from '../../../admin/utils/api';
import '../../styles/admin-common.css';
import './FacultyInformation.css';

const FacultyInformation = () => {
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', designation: '', profile_picture: null });
  const [editingId, setEditingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [userRole, setUserRole] = useState('');
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Get user role from token
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
    fetchFaculty();
  }, [statusFilter]);

  const fetchFaculty = async () => {
    setLoading(true);
    setError('');
    try {
      // Superadmin always fetches all records (including pending) by default
      // Web coordinators only get approved records
      const url = userRole === 'superadmin'
        ? `${API_BASE}/api/admin/faculty` // Superadmin sees all by default
        : `${API_BASE}/api/admin/faculty`; // Web coordinators see only approved
      const res = await fetch(url, {
        headers: authHeaders(),
      });
      
      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}. ${text.substring(0, 100)}`);
      }
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to load faculty');
      }
      setFaculty(data.faculty || []);
    } catch (err) {
      setError(err.message || 'Failed to load faculty. Please check if the server is running.');
      console.error('Error fetching faculty:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFacultyLogs = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/faculty/${id}/logs`, {
        headers: authHeaders(),
      });
      
      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to load logs');
      }
      setLogs(data.logs || []);
      setShowLogs(true);
    } catch (err) {
      setError(err.message || 'Failed to load logs');
      console.error('Error fetching logs:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 250 * 1024) {
        setError('Image size must be less than 250 KB');
        return;
      }
      setForm((prev) => ({ ...prev, profile_picture: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setForm({ name: '', designation: '', profile_picture: null });
    setEditingId(null);
    setPreviewImage(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.name || !form.designation) {
      setError('Name and designation are required');
      return;
    }

    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('designation', form.designation);
    if (form.profile_picture) {
      formData.append('profile_picture', form.profile_picture);
    }

    try {
      const url = editingId
        ? `${API_BASE}/api/admin/faculty/${editingId}`
        : `${API_BASE}/api/admin/faculty`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: formData,
      });

      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to save faculty');
      }

      setSuccess(data.message || 'Faculty saved successfully');
      resetForm();
      
      if (editingId) {
        // Force page reload after editing to show updated status
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        await fetchFaculty();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (facultyMember) => {
    setForm({
      name: facultyMember.name,
      designation: facultyMember.designation,
      profile_picture: null,
    });
    setEditingId(facultyMember.id);
    setPreviewImage(facultyMember.profile_picture ? `${API_BASE}${facultyMember.profile_picture}` : null);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this faculty member?')) {
      return;
    }

    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/faculty/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to delete faculty');
      }

      setSuccess('Faculty deleted successfully');
      await fetchFaculty();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = async (id) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/faculty/${id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });

      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to approve faculty');
      }

      setSuccess('Faculty approved successfully');
      await fetchFaculty();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Are you sure you want to reject this faculty member?')) {
      return;
    }

    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/faculty/${id}/reject`, {
        method: 'POST',
        headers: authHeaders(),
      });

      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to reject faculty');
      }

      setSuccess('Faculty rejected');
      await fetchFaculty();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMakeLive = async (id) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/faculty/${id}/make-live`, {
        method: 'POST',
        headers: authHeaders(),
      });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to make live');
      }

      setSuccess('Faculty made live successfully');
      await fetchFaculty();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/faculty/export/csv`, {
        headers: authHeaders(),
      });

      if (!res.ok) {
        throw new Error('Failed to export faculty data');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `faculty_export_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess('Faculty data exported successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setImporting(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('csv_file', file);

    try {
      const res = await fetch(`${API_BASE}/api/admin/faculty/import/csv`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });

      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to import faculty data');
      }

      setSuccess(data.message || 'Faculty data imported successfully');
      if (data.errors && data.errors.length > 0) {
        setError(`Some rows failed: ${data.errors.join(', ')}`);
      }
      await fetchFaculty();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  const downloadSample = () => {
    const csvContent = `Name,Designation
Dr. John Doe,Professor
Dr. Jane Smith,Associate Professor
Dr. Robert Johnson,Assistant Professor
Prof. Mary Williams,Professor
Dr. David Brown,Associate Professor`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'faculty_import_sample.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const pendingFaculty = faculty.filter((f) => f.status === 'pending');
  const approvedFaculty = faculty.filter((f) => f.status === 'approved');
  const rejectedFaculty = faculty.filter((f) => f.status === 'rejected');

  return (
    <div className="admin-page faculty-information-page">
      <div className="page-heading">
        <h1>Faculty Information</h1>
        <p>Manage faculty profiles, designations, and profile pictures.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      {/* Action Buttons */}
      <div className="admin-actions">
        <button onClick={handleExport} disabled={exporting} className="btn-secondary">
          {exporting ? 'Exporting...' : '📥 Export CSV'}
        </button>
        <label className="btn-secondary" style={{ cursor: 'pointer' }}>
          {importing ? 'Importing...' : '📤 Import CSV'}
          <input
            type="file"
            accept=".csv"
            onChange={handleImport}
            disabled={importing}
            style={{ display: 'none' }}
          />
        </label>
        <button onClick={downloadSample} className="btn-secondary">
          📋 Download Sample CSV
        </button>


      </div>

      <div className="admin-grid faculty-grid">
        {/* Form Card */}
        <div className="card">
          <h2>{editingId ? 'Edit Faculty' : 'Add New Faculty'}</h2>
          <form onSubmit={handleSubmit} className="admin-form faculty-form">
            <label>
              Name of Faculty *
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="e.g., Dr. John Doe"
              />
            </label>

            <label>
              Faculty Designation *
              <input
                type="text"
                name="designation"
                value={form.designation}
                onChange={handleChange}
                required
                placeholder="e.g., Professor, Associate Professor"
              />
            </label>

            <label>
              Faculty Profile Picture (Max 250 KB, 118x118)
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <p>Preview (will be resized to 118x118)</p>
                </div>
              )}
            </label>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update Faculty' : 'Add Faculty'}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="btn-secondary">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Faculty List Card */}
        <div className="card faculty-list-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h2>Faculty List</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px'}}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="live">Live</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {loading ? (
            <p>Loading faculty...</p>
          ) : faculty.filter(f => statusFilter === 'all' || f.status === statusFilter).length === 0 ? (
            <p>No faculty members found.</p>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {faculty.filter(f => statusFilter === 'all' || f.status === statusFilter).map((f) => (
                  <tr key={f.id}>
                    <td>
                      {f.profile_picture ? (
                        <img
                          src={`${API_BASE}${f.profile_picture}`}
                          alt={f.name}
                          className="faculty-thumb"
                        />
                      ) : (
                        <div className="faculty-thumb-placeholder">No Photo</div>
                      )}
                    </td>
                    <td>{f.name}</td>
                    <td>{f.designation}</td>
                    <td>
                      <span className={`status status-${f.status}`}>
                        {f.status}
                      </span>
                    </td>
                    <td className="actions">
                      <button onClick={() => handleEdit(f)} className="btn-edit">
                        ✏️ Edit
                      </button>
                      <button onClick={() => handleDelete(f.id)} className="btn-delete">
                        🗑️ Delete
                      </button>
                      {userRole === 'superadmin' && f.status === 'pending' && (
                        <button onClick={() => handleApprove(f.id)} className="btn-success">
                          ✅ Approve
                        </button>
                      )}
                      {userRole === 'superadmin' && f.status === 'pending' && (
                        <button onClick={() => handleReject(f.id)} className="btn-danger">
                          ❌ Reject
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Logs Modal */}
      {showLogs && selectedFaculty && (
        <div className="modal-overlay" onClick={() => setShowLogs(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Logs - {selectedFaculty.name}</h2>
              <button onClick={() => setShowLogs(false)} className="modal-close">
                ×
              </button>
            </div>
            <div className="modal-body">
              {logs.length === 0 ? (
                <p>No logs available</p>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Changed By</th>
                      <th>Role</th>
                      <th>IP Address</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td>
                          <span className={`log-action log-${log.action}`}>
                            {log.action}
                          </span>
                        </td>
                        <td>{log.changed_by_name}</td>
                        <td>{log.role}</td>
                        <td>{log.ip_address || 'N/A'}</td>
                        <td>{new Date(log.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyInformation;
